import React from "react";
import {
  createFreeDay,
  fetchFreeDaysByDateRangeAndPartnerIds,
} from "../../../../services/freeDay.service.ts";
import { weekDays, weeks } from "../../../../utils/constanst";
import { useDispatch } from "react-redux";
import { updateOrAddFreeDay } from "../../../../store/slices/scheduleSlice.ts";
import { toCamelCase } from "../../../../hooks/toCameCase.tsx";
import { notification } from "../../../../utils/Notifications.tsx";

interface ModalConfigureFreeDaysProps {
  workers: any[];
  weekSelected: number | string;
  onClose: () => void;
}

const ModalConfigureFreeDays: React.FC<ModalConfigureFreeDaysProps> = ({
  workers,
  weekSelected,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = React.useState(false);
  const [workerFreeDays, setWorkerFreeDays] = React.useState<
    Record<number, number[]>
  >({});
  const [savedWorkerFreeDays, setSavedWorkerFreeDays] = React.useState<
    Record<number, number[]>
  >({});

  const getWeekRange = () => {
    if (typeof weekSelected === "string") {
      return weekSelected;
    }
    return weeks[weekSelected]?.value || "";
  };

  const workersIdsRef = React.useRef<string>("");
  const [displayOrder, setDisplayOrder] = React.useState<number[]>([]);

  React.useEffect(() => {
    const workersIds = workers
      .map((w) => w.id)
      .sort()
      .join(",");

    if (workersIds !== workersIdsRef.current && workers.length > 0) {
      workersIdsRef.current = workersIds;
      const fetchAllFreeDays = async () => {
        const weekRange = getWeekRange();
        if (!weekRange) return;

        try {
          const partnerIds = workers.map((w) => w.id);

          const freeDaysData = await fetchFreeDaysByDateRangeAndPartnerIds(
            weekRange,
            partnerIds
          );

          const freeDaysMap = new Map<number, any>();
          freeDaysData.forEach((freeDay: any) => {
            if (freeDay?.partner?.id) {
              freeDaysMap.set(freeDay.partner.id, freeDay);
            }
          });

          const newWorkerFreeDays: Record<number, number[]> = {};
          workers.forEach((worker) => {
            const freeDayData = freeDaysMap.get(worker.id);
            if (
              freeDayData?.days &&
              Array.isArray(freeDayData.days) &&
              freeDayData.days.length > 0
            ) {
              const indices = freeDayData.days
                .map((dayName: string) =>
                  weekDays.findIndex((d) => d.name === dayName)
                )
                .filter((idx: number) => idx !== -1);
              newWorkerFreeDays[worker.id] = indices;
            } else {
              newWorkerFreeDays[worker.id] = [];
            }
          });

          setWorkerFreeDays(newWorkerFreeDays);
          setSavedWorkerFreeDays(newWorkerFreeDays);
          const savedFirst = workers.filter((w) => (newWorkerFreeDays[w.id] ?? []).length > 0).map((w) => w.id);
          const rest = workers.filter((w) => (newWorkerFreeDays[w.id] ?? []).length === 0).map((w) => w.id);
          setDisplayOrder([...savedFirst, ...rest]);
        } catch (error) {
          console.error("Error fetching free days:", error);

          const errorWorkerFreeDays: Record<number, number[]> = {};
          workers.forEach((worker) => {
            errorWorkerFreeDays[worker.id] = [];
          });
          setWorkerFreeDays(errorWorkerFreeDays);
          setSavedWorkerFreeDays(errorWorkerFreeDays);
          setDisplayOrder(workers.map((w) => w.id));
        }
      };
      fetchAllFreeDays();
    }
  }, [workers, weekSelected]);

  const toggleDayForWorker = (workerId: number, dayIndex: number) => {
    setWorkerFreeDays((prev) => {
      const current = prev[workerId] ?? [];
      const hasDay = current.includes(dayIndex);
      const next = hasDay ? [] : [dayIndex];
      return { ...prev, [workerId]: next };
    });
  };

  const saveAllFreeDays = async () => {
    const weekRange = getWeekRange();
    if (!weekRange) return;

    const workersToSave = workers.filter((w) => {
      const days = workerFreeDays[w.id] ?? [];
      return days.length > 0;
    });
    if (workersToSave.length === 0) {
      notification(
        "Sin cambios",
        "Selecciona al menos un día libre para algún trabajador",
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      for (const worker of workersToSave) {
        const selectedIndices = workerFreeDays[worker.id] ?? [];
        const dayNames = selectedIndices.map((i) => weekDays[i].name);
        await createFreeDay({
          partnerId: worker.id,
          days: dayNames,
          dateRange: weekRange,
        });
        dispatch(
          updateOrAddFreeDay({
            id: worker.id,
            freeDays: selectedIndices,
          })
        );
      }
      setSavedWorkerFreeDays((prev) => ({ ...prev, ...workerFreeDays }));
      notification(
        "Éxito",
        `Días libres guardados para ${workersToSave.length} trabajador(es)`,
        "success"
      );
      onClose();
    } catch (error) {
      console.error("Error saving free days:", error);
      notification("Error", "No se pudieron guardar los días libres", "danger");
    } finally {
      setSaving(false);
    }
  };

  const isSavedSelection = (workerId: number) => {
    const current = (workerFreeDays[workerId] ?? []).slice().sort((a, b) => a - b);
    const saved = (savedWorkerFreeDays[workerId] ?? []).slice().sort((a, b) => a - b);
    if (current.length !== saved.length) return false;
    return current.every((v, i) => v === saved[i]);
  };

  type CardType = "saved" | "editing" | "unsaved";
  const getCardType = (workerId: number): CardType => {
    const saved = savedWorkerFreeDays[workerId] ?? [];
    if (!isSavedSelection(workerId)) return "editing";
    if (saved.length > 0) return "saved";
    return "unsaved";
  };

  const orderedWorkers =
    displayOrder.length > 0
      ? displayOrder.map((id) => workers.find((w) => w.id === id)).filter(Boolean) as typeof workers
      : workers;

  const WorkerCard = ({
    worker,
    selectedDays,
    onDayToggle,
    cardType,
  }: {
    worker: any;
    selectedDays: number[];
    onDayToggle: (dayIndex: number) => void;
    cardType: CardType;
  }) => {
    const isSaved = cardType === "saved";
    const isEditing = cardType === "editing";
    const isUnsaved = cardType === "unsaved";

    const backgroundColor = isSaved
      ? "rgba(40, 167, 69, 0.15)"
      : isEditing
      ? "rgba(40, 167, 69, 0.06)"
      : "rgba(108, 117, 125, 0.12)";
    const borderColor = isSaved
      ? "rgba(40, 167, 69, 0.5)"
      : isEditing
      ? "rgba(40, 167, 69, 0.45)"
      : "rgba(108, 117, 125, 0.35)";
    const borderStyle = isEditing ? "dashed" : "solid";

    return (
      <div
        className="card mb-3"
        style={{
          border: `2px ${borderStyle} ${borderColor}`,
          borderRadius: "8px",
          backgroundColor,
          opacity: isEditing ? 0.95 : 1,
        }}
      >
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h6 className="mb-1 fw-600">
                {toCamelCase(
                  `${worker.person.firstName} ${worker.person.lastName}`
                )}
              </h6>
              <small className="text-muted">
                {worker.candidate?.vacancy?.position?.name || "Sin posición"}
              </small>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-3">
            {weekDays.map((day, index) => (
              <button
                key={index}
                type="button"
                onClick={() => onDayToggle(index)}
                className={`btn ${
                  selectedDays.includes(index)
                    ? "btn-primary"
                    : "btn-outline-secondary"
                } rounded-pill`}
                title={day.name}
                disabled={saving}
                style={{
                  width: "35px",
                  height: "35px",
                  textTransform: "capitalize",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontSize: "12px",
                  padding: 0,
                }}
              >
                {day.name.slice(0, 1)}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          .modal-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .modal-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      <div
        className="modal-scrollbar"
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          position: "relative",
        }}
      >
        <div className="mb-4" style={{ paddingBottom: "72px" }}>
          {workers.length === 0 ? (
            <div className="alert alert-info mb-0">
              <i className="fa-solid fa-info-circle me-2"></i>
              No hay trabajadores en esta sucursal.
            </div>
          ) : (
            <div className="row">
              {orderedWorkers.map((worker) => (
                <div key={worker.id} className="col-md-6">
                  <WorkerCard
                    worker={worker}
                    selectedDays={workerFreeDays[worker.id] ?? []}
                    onDayToggle={(dayIndex) => toggleDayForWorker(worker.id, dayIndex)}
                    cardType={getCardType(worker.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          className="d-flex justify-content-end gap-2 pt-3 border-top bg-white"
          style={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            paddingBottom: "0.5rem",
            zIndex: 10,
            boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cerrar
          </button>
          <button
            className="btn btn-primary"
            onClick={saveAllFreeDays}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk me-2"></i>
                Guardar
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default ModalConfigureFreeDays;
